import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';
import { z } from 'zod';

import { DocumentSet } from '@/components/CodebaseView/DocumentSet/DocumentSet';
import { SidebarCodebase } from '@/components/CodebaseView/SidebarCodebase';
import { LeftSidebarPortal } from '@/components/portals/LeftSidebarPortal';
import { useCodebase } from '@/contexts/CodebaseContext';
import { TocProvider, useToc } from '@/contexts/TocContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useCodebaseRoute } from '@/hooks/useCodebaseRoute';
import { searchParam } from '@/utils/router';

const treeSearchSchema = z.object({
  branchId: searchParam().optional(),
  versionId: searchParam().optional(),
});

export const Route = createFileRoute('/$organizationName/cb/$codebaseId/tree/$')({
  component: CodebaseTreePage,
  validateSearch: zodValidator(treeSearchSchema),
});

function CodebaseTreePage() {
  return (
    <TocProvider>
      <TreeContent />
    </TocProvider>
  );
}

function TreeContent() {
  const { captureEvent } = useAnalytics();
  const { path: routePath, pathArray: routePathArray, codebaseId } = useCodebaseRoute();
  const { codebase, codebaseName } = useCodebase();

  const {
    activeTab,
    setActiveTab,
    sectionTitles,
    handleTabClick,
    onEditorReady,
    tocItems,
    editor,
    lastClickedSectionRef,
  } = useToc();

  useEffect(() => {
    captureEvent('docs_viewed', { codebase_id: codebaseId });
  }, [captureEvent, codebaseId]);

  // Fallback to codebaseName if routePath is not provided.
  const path = routePath || codebaseName;
  const pathArray = routePathArray || [codebaseName];

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-y-6 px-8 pt-4 pb-4">
      <LeftSidebarPortal.Mount>
        <SidebarCodebase path={path} tocItems={tocItems} editor={editor} onTocClick={handleTabClick} />
      </LeftSidebarPortal.Mount>
      <DocumentSet
        browsable={codebase?.browsable}
        path={path}
        pathArray={pathArray}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        sectionTitles={sectionTitles}
        onEditorReady={onEditorReady}
        lastClickedSectionRef={lastClickedSectionRef}
      />
    </div>
  );
}
